import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import config from '../../../../config.js';
import {
  BlueprintDownloadResponse,
  GenerateBlueprintResponse,
} from './blueprint.models';
import { pollExistingJob } from '../../../shared/jobs/poll-job';
import { JobStatusResponse } from '../../../shared/jobs/job.models';

/**
 * BlueprintService — polls the tool-api Solution Blueprint job that is
 * auto-triggered when document intake analysis finishes, and downloads the
 * generated DOCX once it's ready.
 */
@Injectable({
  providedIn: 'root',
})
export class BlueprintService {
  private apiUrl = `${config.API_GATEWAY_ENDPOINT}/blueprints`;

  constructor(private http: HttpClient) {}

  /**
   * Polls `GET /blueprints/{jobId}` until the auto-triggered blueprint job
   * finishes. Generation runs server-side via the AI gateway and typically
   * takes 30–120 seconds — by the time the user reaches the Architecture
   * page, the job may already be done, in which case this resolves on the
   * first poll. Callers should show a persistent progress state while this
   * is in flight.
   */
  pollBlueprint(jobId: string): Observable<GenerateBlueprintResponse> {
    return pollExistingJob<GenerateBlueprintResponse>(jobId, (id) =>
      this.getBlueprintStatus(id),
    );
  }

  /** GETs the current status of a previously submitted blueprint job. */
  private getBlueprintStatus(jobId: string): Observable<JobStatusResponse> {
    return this.http.get<JobStatusResponse>(`${this.apiUrl}/${jobId}`);
  }

  /** GETs a presigned S3 URL (valid for `expires_in` seconds) for the generated DOCX. */
  getDownloadUrl(jobId: string): Observable<BlueprintDownloadResponse> {
    return this.http.get<BlueprintDownloadResponse>(`${this.apiUrl}/${jobId}/download`);
  }

  /**
   * Triggers a browser download of the generated document by opening the
   * presigned S3 URL directly — a plain navigation, not an XHR, so it never
   * touches S3 CORS.
   */
  download(jobId: string): Observable<void> {
    return this.getDownloadUrl(jobId).pipe(
      tap((response) => {
        window.open(response.download_url, '_blank');
      }),
      map(() => undefined),
    );
  }
}

